import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useImpersonation from '../../hooks/useImpersonation';
import {
  MdCheckCircle, MdCancel, MdHourglassTop, MdSearch, MdDelete,
  MdThumbUp, MdThumbDown, MdEmail, MdPhone, MdSchedule,
} from 'react-icons/md';
import Button from '../../components/common/Button';
import {
  getApplications, getApprovalStats, approveUser, rejectUser, deleteApplication,
} from '../../services/approvalsService';

const TABS = [
  { key: 'pending',  label: 'Pending',  icon: MdHourglassTop },
  { key: 'approved', label: 'Approved', icon: MdCheckCircle  },
  { key: 'rejected', label: 'Rejected', icon: MdCancel       },
];

// registrationMethod enum is only ['invite','self_signup'] — no dead "trial" badge.
const METHOD_BADGE = {
  self_signup: { label: 'Self Signup', cls: 'bg-nav-hover text-muted border border-divider' },
  // Both are neutral (how they signed up, not a status) — indigo had no dark-mode override
  // (invisible/washed-out on dark) and wasn't the right meaning anyway; matched to self_signup's
  // neutral token treatment, just the more-emphasized tone so the two remain distinguishable.
  invite:      { label: 'Invited',     cls: 'bg-nav-hover text-secondary border border-divider' },
};

function relativeTime(date) {
  if (!date) return '';
  const diff  = Date.now() - new Date(date).getTime();
  const days  = Math.floor(diff / 86_400_000);
  const hours = Math.floor(diff / 3_600_000);
  if (days === 0) return hours < 1 ? 'just now' : `${hours}h ago`;
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

// ── Reject modal ──────────────────────────────────────────────────────────────
function RejectModal({ user, onClose, onConfirm, loading }) {
  const [reason, setReason] = useState('');
  const tooShort = reason.trim().length > 0 && reason.trim().length < 10;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-card rounded-3xl shadow-2xl p-6 max-w-sm w-full border border-divider">
        <h3 className="font-bold text-secondary text-lg mb-1">Reject {user.name}?</h3>
        <p className="text-subtle text-sm mb-4">
          A reason is required (min 10 characters). It will be emailed to the applicant.
        </p>
        <textarea
          rows={3}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Incomplete information, unverifiable contact details…"
          className="w-full border border-divider rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 mb-1 bg-card text-secondary placeholder:text-subtle"
        />
        {tooShort && <p className="text-xs text-red-500 mb-3">Reason must be at least 10 characters.</p>}
        {!tooShort && <div className="mb-3" />}
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>Cancel</Button>
          <Button
            fullWidth
            onClick={() => onConfirm(reason)}
            loading={loading}
            disabled={reason.trim().length < 10}
            className="!bg-red-500 hover:!bg-red-600"
          >
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Approve confirm dialog ────────────────────────────────────────────────────
function ConfirmApproveDialog({ user, onClose, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-card rounded-3xl shadow-2xl p-6 max-w-sm w-full border border-divider">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-emerald-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <MdCheckCircle className="text-emerald-400 text-xl" />
          </div>
          <div>
            <h3 className="font-bold text-secondary text-base">Approve {user.name}?</h3>
            <p className="text-subtle text-xs">They will receive a welcome email.</p>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" fullWidth onClick={onClose}>Cancel</Button>
          <Button fullWidth onClick={onConfirm} loading={loading} className="!bg-green-600 hover:!bg-green-700">
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirm dialog (destructive) ───────────────────────────────────────
function ConfirmDeleteDialog({ user, onClose, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-card rounded-3xl shadow-2xl p-6 max-w-sm w-full border border-divider">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-red-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <MdDelete className="text-red-400 text-xl" />
          </div>
          <div>
            <h3 className="font-bold text-secondary text-base">Delete application?</h3>
            <p className="text-subtle text-xs">
              This permanently removes {user.name}'s application and cannot be undone.
            </p>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" fullWidth onClick={onClose}>Cancel</Button>
          <Button fullWidth onClick={onConfirm} loading={loading} className="!bg-red-500 hover:!bg-red-600">
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Applicant card ────────────────────────────────────────────────────────────
function ApplicantCard({ user, tab, onApprove, onReject, onDelete }) {
  const method = METHOD_BADGE[user.registrationMethod] || METHOD_BADGE.invite;
  const { isReadOnly } = useImpersonation();
  const isTrainer = user.role === 'trainer';
  const roleCls = isTrainer ? 'bg-emerald-500/15 text-emerald-400' : 'bg-sky-500/15 text-sky-400';
  const roDisabled = isReadOnly ? 'Disabled: read-only support mode' : undefined;

  return (
    <div className="bg-card border border-divider rounded-xl p-4 flex items-start gap-4 hover:bg-nav-hover/40 transition-colors">
      {/* Avatar */}
      <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
        {(user.name || '?')[0].toUpperCase()}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-secondary text-sm">{user.name}</p>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${method.cls}`}>
            {method.label}
          </span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${roleCls}`}>
            {user.role}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[11px] text-muted">
          <span className="inline-flex items-center gap-1 min-w-0">
            <MdEmail className="text-subtle flex-shrink-0" /> <span className="truncate">{user.email}</span>
          </span>
          {user.phone && (
            <span className="inline-flex items-center gap-1"><MdPhone className="text-subtle" /> {user.phone}</span>
          )}
          {user.gender && <span className="capitalize">{user.gender}</span>}
          <span className="inline-flex items-center gap-1">
            <MdSchedule className="text-subtle" /> Applied {relativeTime(user.appliedAt || user.createdAt)}
          </span>
        </div>
        {user.registrationIp && (
          <p className="text-[10px] text-subtle mt-1 font-mono">IP: {user.registrationIp}</p>
        )}
        {tab === 'rejected' && user.rejectionReason && (
          <p className="text-xs text-red-400 mt-1.5 truncate">Reason: {user.rejectionReason}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {tab === 'pending' && (
          <>
            <button
              onClick={() => !isReadOnly && onApprove()}
              disabled={isReadOnly}
              title={roDisabled}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MdCheckCircle className="text-sm" /> Approve
            </button>
            <button
              onClick={() => !isReadOnly && onReject()}
              disabled={isReadOnly}
              title={roDisabled}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/15 text-red-400 border border-red-500/20 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MdCancel className="text-sm" /> Reject
            </button>
          </>
        )}
        <button
          onClick={() => !isReadOnly && onDelete()}
          disabled={isReadOnly}
          title={isReadOnly ? roDisabled : 'Delete application'}
          className="p-1.5 bg-card border border-divider text-muted hover:bg-nav-hover hover:text-red-400 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <MdDelete className="text-base" />
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function Approvals() {
  document.title = 'Approvals — TrainAxis';
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab,           setTab]           = useState(() => {
    const t = searchParams.get('tab');
    return TABS.some(x => x.key === t) ? t : 'pending';
  });
  // Persist the active tab to the URL so it survives a refresh (replace → back button stays sane).
  useEffect(() => {
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', tab); return n; }, { replace: true });
  }, [tab, setSearchParams]);
  const [search,        setSearch]        = useState('');
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget,  setRejectTarget]  = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);

  const { data: statsData } = useQuery({
    queryKey: ['approval-stats'],
    queryFn:  () => getApprovalStats().then(r => r.data),
    refetchInterval: 30000,
  });

  const { data, isPending } = useQuery({
    queryKey: ['approvals', tab],
    queryFn:  () => getApplications(tab).then(r => r.data),
    refetchInterval: 30000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['approvals'] });
    queryClient.invalidateQueries({ queryKey: ['approval-stats'] });
    queryClient.invalidateQueries({ queryKey: ['approval-count'] });
  };

  const approveMut = useMutation({
    mutationFn: approveUser,
    onSuccess: () => { toast.success('Member approved — welcome email sent'); setApproveTarget(null); invalidate(); },
    onError:   () => toast.error('Failed to approve'),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }) => rejectUser(id, reason),
    onSuccess: () => { toast.success('Application rejected'); setRejectTarget(null); invalidate(); },
    onError:   (err) => toast.error(err?.response?.data?.message || 'Failed to reject'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteApplication,
    onSuccess: () => { toast.success('Application deleted'); setDeleteTarget(null); invalidate(); },
    onError:   () => toast.error('Failed to delete'),
  });

  const users = (data?.users || []).filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const stats = statsData || { pending: 0, approvedThisMonth: 0, rejectedThisMonth: 0 };

  const EMPTY = {
    pending:  { icon: MdCheckCircle, color: 'text-emerald-400', title: 'All caught up!', sub: 'No pending applications.' },
    approved: { icon: MdThumbUp,     color: 'text-subtle',      title: 'None yet',       sub: 'No approved members yet.' },
    rejected: { icon: MdThumbDown,   color: 'text-subtle',      title: 'None yet',       sub: 'No rejected applications.' },
  };

  return (
    <div className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pending Review',      value: stats.pending,           icon: MdHourglassTop, chip: 'bg-amber-500/12 text-amber-400'   },
          { label: 'Approved This Month', value: stats.approvedThisMonth, icon: MdCheckCircle,  chip: 'bg-emerald-500/12 text-emerald-400' },
          { label: 'Rejected This Month', value: stats.rejectedThisMonth, icon: MdCancel,       chip: 'bg-red-500/12 text-red-400'        },
        ].map(s => (
          <div key={s.label} className="bg-card border border-divider rounded-xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 ${s.chip} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <s.icon className="text-xl" />
            </div>
            <div>
              <p className="text-xl font-bold text-secondary leading-tight">{s.value}</p>
              <p className="text-[11px] text-muted leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs + search */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 bg-nav-hover/40 p-1 rounded-xl">
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  active ? 'bg-card border border-divider shadow-sm text-secondary' : 'text-muted hover:text-secondary'
                }`}
              >
                <t.icon className={`text-sm ${active ? 'text-primary' : ''}`} />
                {t.label}
                {t.key === 'pending' && stats.pending > 0 && (
                  <span className="bg-primary text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                    {stats.pending}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-lg" />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-divider rounded-xl bg-card text-secondary placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary/30 w-56"
          />
        </div>
      </div>

      {/* List */}
      {isPending ? (
        <div className="space-y-2.5">
          {[1, 2, 3].map(i => <div key={i} className="h-[88px] bg-card border border-divider rounded-xl animate-pulse" />)}
        </div>
      ) : !users.length ? (
        <div className="bg-card border border-divider rounded-xl py-16 text-center">
          {(() => { const e = EMPTY[tab]; return (
            <>
              <e.icon className={`text-5xl ${e.color} mx-auto mb-3`} />
              <p className="text-muted font-medium">{e.title}</p>
              <p className="text-subtle text-sm">{e.sub}</p>
            </>
          ); })()}
        </div>
      ) : (
        <div className="space-y-2.5">
          {users.map(u => (
            <ApplicantCard
              key={u._id}
              user={u}
              tab={tab}
              onApprove={() => setApproveTarget(u)}
              onReject={()  => setRejectTarget(u)}
              onDelete={()  => setDeleteTarget(u)}
            />
          ))}
        </div>
      )}

      {approveTarget && (
        <ConfirmApproveDialog
          user={approveTarget}
          onClose={() => setApproveTarget(null)}
          onConfirm={() => approveMut.mutate(approveTarget._id)}
          loading={approveMut.isPending}
        />
      )}

      {rejectTarget && (
        <RejectModal
          user={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onConfirm={(reason) => rejectMut.mutate({ id: rejectTarget._id, reason })}
          loading={rejectMut.isPending}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteDialog
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMut.mutate(deleteTarget._id)}
          loading={deleteMut.isPending}
        />
      )}
    </div>
  );
}

export default Approvals;
